import { fetchData } from '@abb-procure/api';
import {
  computed,
  effect,
  inject,
  Injectable,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormRecord } from '@angular/forms';
import { deepEqual } from 'fast-equals';
import { distinctUntilChanged } from 'rxjs';
import { v4 as uuidV4 } from 'uuid';
import {
  DEFAULT_MAX_RANGE,
  DEFAULT_MIN_RANGE,
  DEFAULTS_PAGE_INDEX,
  DEFAULTS_PAGE_SIZE,
  MIN_INPUT_LENGTH,
  PAGE_INDEX,
  PAGE_SIZE,
  WILDCARD_SEARCH_TERM,
} from '../constants';
import { SearchApiService } from '../services/search-api.service';
import { AvailableFilterOptionDto } from '../types/available-filter-option-dto.type';
import { AvailableSearchOptionDto } from '../types/available-search-options-dto.type';
import { SearchDefaultResponse } from '../types/search-default-response.type';
import { SearchDocumentResponseEntities } from '../types/search-document-response-entities.type';
import {
  SearchFilterRequest,
  searchFilterRequestFactory,
} from '../types/search-filter-request.type';
import { SearchFilterValueDto } from '../types/search-filter-value-dto.type';
import { SearchOptionsResponse } from '../types/search-options-response.type';
import {
  SearchRequest,
  searchRequestFactory,
} from '../types/search-request.type';
import { SearchResponse } from '../types/search-response.type';
import { parseNumber } from '../utils/parse-number.util';

@Injectable({
  providedIn: 'root',
})
export class SearchState {
  readonly searchTerm = signal<string>('');
  readonly data = signal<SearchResponse | null>(null);
  readonly searchVisible = signal<boolean>(false);
  readonly isLoading = signal<boolean>(false);

  readonly activeTab = signal<AvailableSearchOptionDto | null>(null, {
    equal: deepEqual,
  });

  readonly pageSize = signal(PAGE_SIZE);

  readonly currentEntity = computed(() => {
    const entityType = this.activeTab()?.entityType?.toUpperCase();
    const result = this.data()?.result;

    if (!entityType || !result) {
      return null;
    }

    return result[entityType];
  });

  readonly disabledGlobalFilters = computed(
    () => this.activeTab()?.disabledGlobalFilters,
  );

  readonly globalFilters = computed(() => this.options()?.globalFilters);
  readonly tabs = computed(() => this.options()?.tabs);
  readonly favoriteEntities = computed(() => this.defaults()?.favoriteEntities);

  readonly recentlyVisitedEntities = computed(
    () => this.defaults()?.recentlyVisitedEntities,
  );

  readonly globalFilterForm = new FormRecord<
    FormControl | FormRecord<FormControl> | FormGroup
  >({});

  readonly entityFilterForms = new FormRecord<FormGroup>({});

  readonly facetsForms = new FormRecord<FormRecord<FormRecord<FormControl>>>(
    {},
  );

  readonly sessionId = uuidV4();

  readonly hasSearchTerm = computed(
    () =>
      this.searchTerm().length >= MIN_INPUT_LENGTH ||
      this.searchTerm() === WILDCARD_SEARCH_TERM,
  );

  readonly autocomplete = signal<string[]>([]);

  private readonly options = signal<SearchOptionsResponse | null>(null);
  private readonly defaults = signal<SearchDefaultResponse | null>(null);
  private readonly searchApi = inject(SearchApiService);

  constructor() {
    effect(() => {
      this.globalFilters()?.forEach((filter) => {
        this.globalFilterForm.setControl(
          filter.fieldName,
          this.getFormControl(filter),
        );
      });
    });

    effect(() => {
      this.tabs()?.forEach((tab) => {
        if (!tab.entityType) {
          return;
        }

        this.buildEntityForm(tab);
      });
    });

    effect(() => {
      const activeTab = this.activeTab();

      if (!activeTab?.entityType) {
        return;
      }

      this.fetchData();
    });

    effect(() => {
      this.activeTab();
      this.pageSize.set(PAGE_SIZE);
    });

    effect(() => {
      if (this.searchVisible()) {
        return;
      }

      this.searchTerm.set('');
    });

    effect(() => {
      this.buildFacetsForm(this.data());
    });

    effect(() => {
      this.pageSize();
      this.fetchData();
    });

    this.bindFormListeners();
  }

  cleanState(): void {
    this.activeTab.set(null);
    this.data.set(null);
    this.autocomplete.set([]);
  }

  setActiveTab(entityType: SearchDocumentResponseEntities | 'all'): void {
    this.activeTab.set(null);

    const tab = this.tabs()?.find((t) => t.entityType === entityType);

    if (!tab) {
      return;
    }

    this.activeTab.set(tab);
  }

  showSearch(): void {
    this.searchVisible.set(true);
  }

  hideSearch(): void {
    this.searchVisible.set(false);
  }

  async initialise(): Promise<void> {
    const [options, defaults] = await Promise.all([
      this.searchApi.getSearchOptions(),
      this.searchApi.getSearchDefaults(DEFAULTS_PAGE_INDEX, DEFAULTS_PAGE_SIZE),
    ]);

    this.options.set(options);
    this.defaults.set(defaults);
  }

  async fetchData(): Promise<void> {
    const term = untracked(() => this.searchTerm());

    this.data.set(null);

    if (!term.length) {
      return;
    }

    fetchData(
      this.searchApi.search(this.assembleSearch(term)),
      (response) => {
        this.data.set(response);
      },
      this.isLoading,
    );
  }

  async fetchAutocomplete(): Promise<void> {
    const term = untracked(() => this.searchTerm());

    if (!term.length) {
      return;
    }

    const response = await this.searchApi.getAutocomplete(
      this.assembleSearch(term),
    );

    if (!response?.length) {
      this.autocomplete.set([]);
      return;
    }

    this.autocomplete.set(
      response
        .filter((item) => item.length > term.length)
        .map((item) => this.buildAutocompleteSuggestion(term, item)),
    );
  }

  clearAutocomplete(): void {
    this.autocomplete.set([]);
  }

  private buildAutocompleteSuggestion(
    term: string,
    suggestion: string,
  ): string {
    const lowerTerm = term.toLowerCase();
    const lowerSuggestion = suggestion.toLowerCase();

    if (lowerSuggestion.startsWith(lowerTerm)) {
      return suggestion.replace(new RegExp(`^${term}`, 'i'), term);
    }

    const words = term.split(/\s+/);

    if (words.length <= 1) {
      return `${term} ${suggestion}`.trim();
    }

    const prefix = words.slice(0, -1).join(' ');

    return prefix ? `${prefix} ${suggestion}` : suggestion;
  }

  private assembleSearch(searchTerm: string): SearchRequest {
    const entityType = this.activeTab()?.entityType ?? 'all';

    const filters: SearchFilterRequest[] = [
      ...this.getGlobalFilters(),
      ...this.getEntityFilters(entityType as SearchDocumentResponseEntities),
      ...this.getFacetFilters(entityType as SearchDocumentResponseEntities),
    ];

    return searchRequestFactory({
      searchTerm,
      entityType,
      filters,
      pageIndex: PAGE_INDEX,
      pageSize: this.pageSize(),
      sessionId: this.sessionId,
      requestId: uuidV4(),
    });
  }

  private getEntityFilters(
    entityType: SearchDocumentResponseEntities,
  ): SearchFilterRequest[] {
    const filters = this.tabs()?.find(
      (t) => t.entityType === entityType,
    )?.filters;

    if (!filters) {
      return [];
    }

    return this.getSearchValue(
      this.entityFilterForms.controls[entityType],
      filters,
    );
  }

  private getFacetFilters(
    entityType: SearchDocumentResponseEntities,
  ): SearchFilterRequest[] {
    const facets =
      this.facetsForms.controls[entityType.toUpperCase()]?.controls;

    if (!facets) {
      return [];
    }

    return Object.keys(facets)
      .filter(
        (field) =>
          !(
            entityType.toLowerCase() === 'sup' &&
            field === 'qualification_status/id'
          ),
      )
      .reduce((acc, facet) => {
        const values =
          Object.entries(facets[facet].getRawValue())
            .filter(([_, value]) => value)
            .map(([key]) => key) ?? [];

        return [
          ...acc,
          {
            entityType,
            fieldName: facet,
            dataType: 'string',
            uiType: 'Checkbox',
            order: null,
            values,
          },
        ];
      }, [])
      .filter((filter) => filter.values.length > 0);
  }

  private getGlobalFilters(): SearchFilterRequest[] {
    const globalFilters = this.globalFilters();

    if (!globalFilters) {
      return [];
    }

    return this.getSearchValue(
      this.globalFilterForm,
      globalFilters.filter(
        (filter) => !this.disabledGlobalFilters()?.includes(filter.fieldName),
      ),
    );
  }

  private getFormControl(
    filter: AvailableFilterOptionDto,
  ): FormRecord<FormControl> | FormControl {
    if (filter.uiType === 'Checkbox') {
      const record = new FormRecord<FormControl>({});

      filter.values.forEach((value) => {
        record.addControl(value.id, new FormControl(value.defaultSelected));
      });

      return record;
    }

    if (filter.uiType === 'NumberRange') {
      return new FormGroup({
        min: new FormControl<number | null>(
          parseNumber(filter.values, 'min') ?? DEFAULT_MIN_RANGE,
        ),
        max: new FormControl<number | null>(
          parseNumber(filter.values, 'max') ?? DEFAULT_MAX_RANGE,
        ),
      });
    }

    return new FormControl(
      filter.values.filter((item) => item.defaultSelected),
    );
  }

  private getSearchValue(
    group: FormRecord,
    filters: AvailableFilterOptionDto[],
  ): SearchFilterRequest[] {
    return filters
      .reduce((acc, filter) => {
        const control = group.controls[filter.fieldName];

        if (!control) {
          return acc;
        }

        const { value } = control;

        if (control instanceof FormControl || control instanceof FormRecord) {
          const selected =
            control instanceof FormRecord
              ? Object.keys(value).filter((key) => value[key])
              : value.map((item: SearchFilterValueDto) => item.id);

          return [
            ...acc,
            searchFilterRequestFactory(
              filter,
              filter.values.filter((item) => selected.includes(item.id)),
            ),
          ];
        }

        return [
          ...acc,
          searchFilterRequestFactory(
            filter,
            filter.values.map((item) => ({
              ...item,
              id: `${value[item.id] ?? item.id}`,
            })),
          ),
        ];
      }, [])
      .filter((filter) => filter.values.length > 0);
  }

  private buildEntityForm(tab: AvailableSearchOptionDto): void {
    const form = new FormRecord<
      FormControl | FormRecord<FormControl> | FormGroup
    >({});

    tab.filters?.forEach((filter) => {
      form.setControl(filter.fieldName, this.getFormControl(filter));
    });

    this.entityFilterForms.setControl(
      tab.entityType as SearchDocumentResponseEntities,
      form,
    );
  }

  private buildFacetsForm(data: SearchResponse | null): void {
    const result = data?.result;

    if (!result) {
      return;
    }

    Object.keys(result).forEach((entityType) => {
      const form = new FormRecord<FormRecord<FormControl>>({});

      result[entityType].facets?.forEach((facet) => {
        if (
          entityType.toLowerCase() === 'sup' &&
          facet.fieldName === 'qualification_status/id'
        ) {
          return;
        }
        const record = new FormRecord<FormControl>({});

        facet.items?.forEach((item) => {
          record.setControl(
            item.value,
            new FormControl<boolean>(
              this.facetsForms.controls[entityType]?.controls[facet.fieldName]
                ?.controls[item.value]?.value ?? false,
            ),
          );
        });

        form.setControl(facet.fieldName, record);
      });

      this.facetsForms.setControl(
        entityType as SearchDocumentResponseEntities,
        form,
      );
    });
  }

  private bindFormListeners(): void {
    this.facetsForms.valueChanges
      .pipe(distinctUntilChanged(deepEqual), takeUntilDestroyed())
      .subscribe(() => {
        this.fetchData();
      });

    this.entityFilterForms.valueChanges
      .pipe(distinctUntilChanged(deepEqual), takeUntilDestroyed())
      .subscribe(() => {
        this.fetchData();
      });

    this.globalFilterForm.valueChanges
      .pipe(distinctUntilChanged(deepEqual), takeUntilDestroyed())
      .subscribe(() => {
        this.fetchData();
      });
  }
}
